import React, { useEffect, useState, useCallback } from "react";
import { hub } from "../../pages/HubPage";
import "./TeamNewsSection.css";

/**
 * Team News HOME surface. Renders the single currently-published Team
 * News item — or a clean empty state when nothing is published. Never
 * shows drafts or archived items; the API filters those on the server.
 */
export function TeamNewsSection({ initial } = {}) {
  // `initial` is passed by HubHomePage after its pre-hydration pass:
  //   • `undefined` → prehydration hadn't resolved by the reveal cap;
  //     we render our own loading skeleton and fetch normally.
  //   • `null`      → prehydration resolved, no current team news;
  //     we render the empty state immediately (no fetch).
  //   • object      → prehydration returned news; render it (no fetch).
  const seeded = initial !== undefined;
  const [state, setState] = useState({
    loading: !seeded,
    news: seeded ? initial : null,
    err: "",
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await hub.get("/hub/team-news/current");
      setState({ loading: false, news: data?.news || null, err: "" });
    } catch (err) {
      // Public endpoint failure — surface the empty state rather than
      // a technical error; the section is not critical to HOME.
      setState({ loading: false, news: null, err: "" });
    }
  }, []);

  useEffect(() => {
    // Skip the initial fetch when the parent has already prehydrated
    // us. Subsequent user actions (e.g. a manual refresh trigger) can
    // still call `load` locally if we ever add one.
    if (seeded) return;
    load();
  }, [load, seeded]);

  const news = state.news;
  const hasCta = !!(news && news.external_url);
  const ctaLabel = (news?.link_label || "").trim() || "VIEW POST";

  const publishedFmt = news?.published_at
    ? new Date(news.published_at).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
      })
    : "";

  return (
    <section className="hh-section hh-section--news" data-testid="hh-team-news">
      <div className="hh-section-head">
        <div className="hh-section-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.4">
            <path d="M4 5h13a3 3 0 0 1 3 3v11H7a3 3 0 0 1-3-3V5z" strokeLinejoin="round"/>
            <path d="M8 10h8M8 14h6"/>
          </svg>
        </div>
        <h2 className="hh-section-title">TEAM NEWS</h2>
      </div>

      {state.loading ? (
        <div className="hh-section-body" data-testid="team-news-loading">
          <div className="hh-skeleton" style={{ height: 120 }} />
        </div>
      ) : !news ? (
        <div className="hh-section-body" data-testid="team-news-empty">
          <div className="hh-empty">
            <div className="hh-empty-title">NO TEAM NEWS YET</div>
            <div className="hh-empty-sub">
              Announcements from the Prophets team will appear here.
            </div>
          </div>
        </div>
      ) : (
        <article className="hh-section-body tn-card" data-testid="team-news-card">
          {news.image_url ? (
            <div className="tn-image-wrap" data-testid="team-news-image">
              <img
                src={news.image_url}
                alt=""
                className="tn-image"
                loading="lazy"
              />
            </div>
          ) : null}

          <div className="tn-body">
            <h3 className="tn-title" data-testid="team-news-title">
              {news.title}
            </h3>
            <p className="tn-text" data-testid="team-news-body">
              {news.body}
            </p>

            <div className="tn-footer">
              {publishedFmt && (
                <span className="tn-date" data-testid="team-news-date">
                  {publishedFmt}
                </span>
              )}
              {hasCta && (
                <a
                  href={news.external_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="tn-cta"
                  data-testid="team-news-cta"
                >
                  {ctaLabel}
                </a>
              )}
            </div>
          </div>
        </article>
      )}
    </section>
  );
}

export default TeamNewsSection;
